<template>
  <div
    v-show="isOpen"
    class="section"
  >
    <div class="buttons">
      <button
        v-if="isEditMode && !noReset"
        class="button"
        @click.prevent="reset()"
      >
        <Undo class="undo" />
      </button>
      <button
        v-if="isEditMode && section.closeDefault"
        class="button"
        @click.prevent="close()"
      >
        <Icon
          name="close"
          class="close"
          :width="20"
          :height="20"
          :scale="1.2"
        >
          <Close />
        </Icon>
      </button>
    </div>
    <h4 class="cm-title">
      <template v-if="section.label">
        {{ $t(`loyalty.sections.${section.label}`) }}
      </template>
      <template v-else>
        {{ $t(`loyalty.sections.${section.name}`) }}
      </template>
    </h4>
    <div>
      <slot />
    </div>
  </div>
</template>

<script>
import Icon from "@/manzana_library/components/Elements/Icon.vue";
import Undo from "@/components/loyalty/entity/campaign/components/Icons/Undo.vue";
import Close from "@/manzana_library/components/Icons/Close.vue";
import {mapMutations, mapState} from "vuex";
import LoyaltyService from "@/services/LoyaltyService";

export default {
  name: "CampaignSection",

  components: {
    Undo,
    Icon,
    Close,
  },

  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    section: {
      type: Object,
      required: true,
    },
    noReset: Boolean,
  },

  data() {
    return {
      isOpen: true,
    };
  },

  computed: {
    ...mapState({
      info: state => state.loyalty.info,
      infoMetaData: state => state.loyalty.infoMetaData,
      defaultValues: state => state.loyalty.defaultValues,
      isEditMode: state => state.loyalty.isEditMode,
      showSections: state => state.loyalty.showSections,
      initialInfo: state => state.loyalty.initialInfo,
      allSections: state => state.loyalty.allSections,
      entitiesCount: state => state.loyalty.entitiesCount,
    }),

    sectionsTabs() {
      return this.allSections ? this.allSections.filter(i => i.tabs).map(i => i.tabs).flat() : [];
    },

    isProbabilityType() {
      return this.info.attributes.IsProbabilityType;
    },
  },

  watch: {
    isProbabilityType() {
      if (this.section.name === "chargeRuleParameters") {
        this.loyaltyMutation({key: "showSections", value: {...this.showSections, timeSumLevels:  false}});
      }
    },
    isEditMode() {
      if (!this.isEditMode) {
        let t = false;
        if (this.section.tabs) this.section.tabs.forEach(i => {
          if (this.entitiesCount.find(item => item.entityName === i.entityName && item.mark === i.mark).count > 0) {
            this.isOpen = true;
            t = true;
          }
        });
        this.fields.forEach(i => {
          const name = i;
          if (this.infoMetaData.attributes[name].type === 5 ?
            (Number(this.info.attributes[name]) !== Number(this.defaultValues.attributes[name])) :
            String(this.info.attributes[name]) !== String(this.defaultValues.attributes[name])) {
            if (
              (this.defaultValues.attributes[name] === null && !["", 0, null, false].includes(this.info.attributes[name]))
            || (this.defaultValues.attributes[name] === "false" && this.info.attributes[name] !== false)
            || (this.infoMetaData.attributes[name].type === 6 && this.dateFormatCondition(name))
            || (![null, "false"].includes(this.defaultValues.attributes[name]) && this.infoMetaData.attributes[name].type !== 6)
            )
            {
              this.isOpen = true;
              t = true;
            }
          }
        });
        if (!t && this.section.closeDefault) this.isOpen = false;
      }
    },

    isOpen() {
      if (this.section.closeDefault) {
        this.loyaltyMutation({key: "showSections", value: {...this.showSections, [this.section.name]: this.isOpen}});
      }
    },

    showSections() {
      if (this.section.name === "timeSumLevels" && this.isProbabilityType) {
        this.isOpen = this.showSections[this.section.name];
      }
      if (this.showSections && this.section.closeDefault && this.showSections[this.section.name]) {
        this.isOpen = this.showSections[this.section.name];
      }
    },

    entitiesCount: {
      handler() {
        if (this.section.tabs && this.entitiesCount && this.entitiesCount.length === this.sectionsTabs.length) {
          this.section.tabs.forEach(i => {
            if (this.entitiesCount.find(item => item.entityName === i.entityName && item.mark === i.mark).count > 0) {
              this.isOpen = true;
            }
          });
        }
      },
      deep: true,
    },
  },

  mounted() {
    if (this.section.closeDefault) this.isOpen = false;

    const syntheticFields = this.section.generalConfig && this.section.generalConfig.reduce((acc, field) => {
      if (field.isSyntheticField) {
        acc.push(...field.syntheticFields);
      }
      return acc;
    }, []);

    if (syntheticFields && syntheticFields.some(field => this.info.attributes[field] !== false)) {
      this.isOpen = true;
    }
    this.fields.forEach(i => {
      const name = i;

      if (!this.hasDefaultValue(name)) {
        if (
          (this.defaultValues.attributes[name] === null && !["", 0, null, false].includes(this.info.attributes[name]))
        || (this.defaultValues.attributes[name] === "false" && this.info.attributes[name] !== false)
        || (this.infoMetaData.attributes[name].type === 6 && this.dateFormatCondition(name))
        || (![null, "false"].includes(this.defaultValues.attributes[name]) && this.infoMetaData.attributes[name].type !== 6)
        )
        {
          this.isOpen = true;
        }
      }
    });

    if (this.section.closeDefault && !this.isOpen && this.section.tabs) {
      this.checkRelationshipsCount();
    }
  },

  methods: {
    ...mapMutations({
      loyaltyMutation: "loyaltyMutation",
    }),

    reset() {
      let vm = this;
      this.fields.forEach(function(item) {
        vm.info.attributes[item] = vm.defaultValues.attributes[item];
      });
    },

    close() {
      this.reset();
      this.isOpen = false;
    },

    dateFormatCondition(name) {
      return !(this.infoMetaData.attributes[name].limitations.dateTimeFormat === 1 ?
        this.$dayjs(this.info.attributes[name]).format('DD.MM.YYYY') === this.$dayjs(this.defaultValues.attributes[name]).format('DD.MM.YYYY') :
        this.$dayjs(this.info.attributes[name]).format('DD.MM.YYYY HH:mm') === this.$dayjs(this.defaultValues.attributes[name]).format('DD.MM.YYYY HH:mm'));
    },

    async checkRelationshipsCount() {
      this.isOpen = await (async () => {
        for (let tab of this.section.tabs) {
          let count = null;
          let entityId = this.info.id;

          if (Array.isArray(this.info.id)) {
            entityId = this.info.id.find(item => item.name === tab.entityFrom).id;
          }

          try {
            count = await LoyaltyService.
              getRelationshipsCount(tab.entityFrom, entityId, tab.entityName, tab.mark);
          } catch (e) {
            console.log(e);
          }

          if (count > 0) {
            return true;
          }
        }
        return false;
      })();
    },

    hasDefaultValue(field) {
      switch (this.infoMetaData.attributes[field].type) {
      case 5:
        return Number(this.info.attributes[field]) === Number(this.defaultValues.attributes[field]);
      case 8:
        return Boolean(this.infoMetaData[field]);
      default:
        return String(this.info.attributes[field]) === String(this.defaultValues.attributes[field]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.section {
  background: $color_white;
  padding: 30px 15px 20px;
  width: 100%;
  position: relative;
  margin-bottom: 20px;

  .row {
    &:not(:last-child) {
      margin-bottom: 10px;
    }

    &.center {
      display: flex;
      justify-content: center;
    }
  }

  .cm-title {
    margin-top: 0;
    margin-bottom: 26px;
    margin-right: 20px;
    position: relative;
  }
}

.buttons {
  position: absolute;
  top: 25px;
  right: 10px;
  display: flex;
  align-items: center;
}

.button {
  width: 25px;
  height: 25px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  margin-left: 0;

  .undo {
    width: 16px;
    height: 16px;
  }

  .add {
    width: 16px;
    height: 16px;
  }
}
</style>
